import React from "react";
import {
  Box,
  Grid,
  Typography,
  Stack,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Alert,
  Tooltip,
  alpha,
  useTheme,
} from "@mui/material";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AddCardDialog from "./payments/AddCardDialog";
import EditCardDialog from "./payments/EditCardDialog";
import ConfirmDialog from "./payments/ConfirmDialog";
import MakePaymentDialog from "./payments/MakePaymentDialog";
import AutoPaySetupDialog from "./payments/AutoPaySetupDialog";
import AutoPayManageDialog from "./payments/AutoPayManageDialog";
import AddBankAccountDialog from "./payments/AddBankAccountDialog";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import { paymentsApi } from "./payments/api";

/**
 * Parent Profile → "Payments & Billing" section.
 *
 * Wires the new admin endpoints (Batch 1D):
 *   GET /admin/parents/{parent}/payments-summary
 *   GET /admin/parents/{parent}/payment-methods
 *   GET /admin/parents/{parent}/linked-student-billing
 *   GET /admin/parents/{parent}/payments
 *
 * Card numbers are never rendered — brand + last-four only.
 * Action buttons are wired stubs; the corresponding modals (Add Card,
 * Make Payment, Set Up Auto-Pay) are follow-on work.
 */

type PaymentsBillingSectionProps = {
  parentId: string | number;
  // Authoritative country for Worldline routing. `null` when the admin
  // hasn't set it yet on the parent — in that case Add Card is blocked
  // with a clear hint rather than defaulting to CA silently.
  parentCountry: "CA" | "US" | null;
};

type Summary = {
  profile_status: string;
  provider: string;
  saved_cards: number;
  active_autopay: number;
  total_paid: number;
};

// Human-readable card brand label. Payment gateways return brands as
// short codes (MC / VI / AX / etc.); normalise the common ones so the
// UI reads as "Mastercard •••• 4821" instead of "MC •••• 4821".
// Unknown values pass through so a new brand never renders blank.
const prettyCardBrand = (raw: string | null): string => {
  const s = String(raw ?? "").trim().toUpperCase();
  if (!s) return "Card";
  const map: Record<string, string> = {
    MC: "Mastercard", MASTERCARD: "Mastercard",
    VI: "Visa", VISA: "Visa",
    AX: "Amex", AMEX: "Amex", "AMERICAN EXPRESS": "Amex",
    DI: "Discover", DISCOVER: "Discover",
    JCB: "JCB",
    DINERS: "Diners", "DINERS CLUB": "Diners",
  };
  return map[s] ?? (raw ?? "Card");
};

type Card = {
  id: number;
  cardBrand: string | null;
  cardLast4: string | null;
  expiryMonth: number | null;
  expiryYear: number | null;
  holderName: string | null;
  isDefault: number | boolean;
  status: string;
};

type LinkedStudent = {
  student_id: number;
  first_name: string;
  last_name: string;
  is_primary_parent: boolean;
  billing_type: string | null;
  monthly_fee: number | null;
  per_class_rate: number | null;
  billing_day: number | null;
  outstanding_balance: number;
  open_invoice_count: number;
  auto_pay: null | {
    agreement_id: number;
    status: string;
    next_charge_date: string | null;
    payer_parent_id: number;
    is_this_parent: boolean;
    card_brand: string | null;
    card_last4: string | null;
  };
};

type PaymentRow = {
  id: number;
  paymentDate: string;
  amount: number;
  status: string;
  source: string | null;
  type: string | null;
  transactionId: string | null;
  provider_transaction_id: string | null;
  payer_name_snapshot: string | null;
  student_first: string | null;
  student_last: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  invoiceNumber: string | null;
};

const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d?: string | null) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch { return String(d).slice(0, 10); }
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
  const s = (status || "").toLowerCase();
  const color =
    s === "active" ? "success" :
    s === "paused" ? "warning" :
    s === "requires_attention" || s === "card_expired" || s === "failed" ? "error" :
    s === "cancelled" ? "default" : "default";
  return <Chip size="small" color={color as any} label={status || "—"} />;
};

const SummaryCard: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode }> = ({ icon, label, value, sub }) => {
  const t = useTheme();
  return (
    <Box sx={{
      p: 2, borderRadius: 2, height: "100%",
      background: alpha(t.palette.primary.main, 0.06),
      border: `1px solid ${alpha(t.palette.divider, 0.6)}`,
    }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{ color: t.palette.primary.main }}>{icon}</Box>
        <Stack>
          <Typography variant="caption" sx={{ color: t.palette.text.secondary, letterSpacing: 0.5, textTransform: "uppercase", fontWeight: 700 }}>{label}</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{value}</Typography>
          {sub && <Typography variant="caption" sx={{ color: t.palette.text.secondary }}>{sub}</Typography>}
        </Stack>
      </Stack>
    </Box>
  );
};

const PaymentsBillingSection: React.FC<PaymentsBillingSectionProps> = ({ parentId, parentCountry }) => {
  const t = useTheme();
  const [summary, setSummary] = React.useState<Summary | null>(null);
  const [cards, setCards] = React.useState<Card[]>([]);
  const [linked, setLinked] = React.useState<LinkedStudent[]>([]);
  const [payments, setPayments] = React.useState<PaymentRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [dlg, setDlg] = React.useState<{ kind: string; payload?: any } | null>(null);

  const fetchAll = React.useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [s, m, l, p] = await Promise.all([
        paymentsApi.summary(parentId),
        paymentsApi.methods(parentId),
        paymentsApi.linkedStudentBilling(parentId),
        paymentsApi.paymentsHistory(parentId, { per_page: 10 }),
      ]);
      setSummary(s);
      setCards(m?.data ?? []);
      setLinked(l?.data ?? []);
      setPayments(p?.data ?? []);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load Payments & Billing.");
    } finally {
      setLoading(false);
    }
  }, [parentId]);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <Box sx={{
      mt: 4, p: 3, borderRadius: 3,
      background: t.palette.background.paper,
      border: `1px solid ${alpha(t.palette.divider, 0.6)}`,
    }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          <CreditCardOutlinedIcon sx={{ verticalAlign: "text-bottom", mr: 1 }} />
          Payments & Billing
        </Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title={parentCountry ? "" : "Set the parent's country before adding a card."} placement="left" arrow>
            <span>
              <Button variant="outlined" size="small" disabled={!parentCountry} onClick={() => setDlg({ kind: "add_card" })}>
                Add Card
              </Button>
            </span>
          </Tooltip>
          {parentCountry === "CA" && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AccountBalanceOutlinedIcon fontSize="small" />}
              onClick={() => setDlg({ kind: "add_bank" })}
            >
              Add Bank Account
            </Button>
          )}
        </Stack>
      </Stack>

      {!parentCountry && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This parent does not have a country set. Edit the parent and choose Canada or United States before adding a payment card.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<AccountBalanceWalletOutlinedIcon />} label="Payment Profile"
            value={loading ? <Skeleton width={120} /> : (summary?.provider ?? "Worldline")}
            sub={loading ? <Skeleton width={80} /> : (summary?.profile_status === "not_set_up" ? "Not Set Up" : summary?.profile_status)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<CreditCardOutlinedIcon />} label="Saved Cards"
            value={loading ? <Skeleton width={40} /> : (summary?.saved_cards ?? 0)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<AutorenewIcon />} label="Auto-Pay"
            value={loading ? <Skeleton width={40} /> : `${summary?.active_autopay ?? 0} Active`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<PaidOutlinedIcon />} label="Total Paid"
            value={loading ? <Skeleton width={80} /> : money(summary?.total_paid ?? 0)}
            sub="all children" />
        </Grid>
      </Grid>

      {/* Saved payment methods */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Saved Payment Methods</Typography>
      {loading ? (
        <Skeleton variant="rounded" height={90} />
      ) : cards.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>No cards saved for this parent yet.</Alert>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {cards.map((c) => (
            <Grid item xs={12} sm={6} md={4} key={c.id}>
              <Box sx={{ p: 2, border: `1px solid ${alpha(t.palette.divider, 0.6)}`, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {prettyCardBrand(c.cardBrand)} •••• {c.cardLast4 || "----"}
                  </Typography>
                  {c.isDefault ? <Chip size="small" color="primary" label="Default" /> : null}
                </Stack>
                {(() => {
                  // Three-line premium layout (spec): brand + last4 on
                  // the primary line, Expires MM/YY on the second line,
                  // cardholder on the third line. Each line hides
                  // itself when the underlying value is missing so we
                  // never render "Expires --/--" or an empty row.
                  const mm = c.expiryMonth != null ? String(c.expiryMonth).padStart(2, "0") : null;
                  const yy = c.expiryYear  != null ? String(c.expiryYear).slice(-2).padStart(2, "0") : null;
                  const expiry = mm && yy ? `${mm}/${yy}` : null;
                  const holder = (c.holderName ?? "").trim();
                  return (
                    <>
                      {expiry && (
                        <Typography variant="body2" sx={{ color: t.palette.text.secondary, mt: 0.25 }}>
                          Expires {expiry}
                        </Typography>
                      )}
                      {holder && (
                        <Typography
                          variant="body2"
                          sx={{ color: t.palette.text.secondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          title={holder}
                        >
                          {holder}
                        </Typography>
                      )}
                    </>
                  );
                })()}
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Button size="small" variant="text" disabled={!!c.isDefault}
                    onClick={async () => {
                      try { await paymentsApi.setDefault(parentId, c.id); fetchAll(); }
                      catch (e: any) { setError(e?.message ?? "Failed"); }
                    }}
                  >Set Default</Button>
                  <Button size="small" variant="text" onClick={() => setDlg({ kind: "edit_card", payload: c })}>Edit</Button>
                  <Button size="small" variant="text" color="error"
                    onClick={() => setDlg({ kind: "delete_card", payload: c })}
                  >Delete</Button>
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Linked Students & Billing */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Linked Students &amp; Billing</Typography>
      {loading ? (
        <Skeleton variant="rounded" height={120} />
      ) : linked.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>No linked students.</Alert>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {linked.map((s) => (
            <Grid item xs={12} md={6} key={s.student_id}>
              <Box sx={{ p: 2, border: `1px solid ${alpha(t.palette.divider, 0.6)}`, borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {s.first_name} {s.last_name}
                    {s.is_primary_parent && (
                      <Chip size="small" color="primary" label="Primary" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Chip size="small" label={s.billing_type ? s.billing_type.replace("_", " ") : "no billing"} />
                </Stack>
                <Typography variant="body2" sx={{ color: t.palette.text.secondary, mt: 0.5 }}>
                  {s.billing_type === "monthly"
                    ? `${money(s.monthly_fee)}/month`
                    : s.billing_type === "class_wise"
                    ? `${money(s.per_class_rate)}/class`
                    : "Billing not configured"}
                  {" · Outstanding "}<strong>{money(s.outstanding_balance)}</strong>
                  {s.open_invoice_count > 0 ? ` (${s.open_invoice_count} open)` : ""}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Auto-Pay:{" "}
                  {s.auto_pay ? (
                    <>
                      <StatusChip status={s.auto_pay.status} />
                      {" — "}Payer: <strong>{s.auto_pay.is_this_parent ? "This parent" : `Parent #${s.auto_pay.payer_parent_id}`}</strong>
                      {" · "}{s.auto_pay.card_brand} •••• {s.auto_pay.card_last4}
                      {s.auto_pay.next_charge_date && (<> · Next {fmtDate(s.auto_pay.next_charge_date)}</>)}
                    </>
                  ) : (
                    <Chip size="small" label="Not Enabled" />
                  )}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
                  <Button size="small" variant="outlined"
                    onClick={() => setDlg({ kind: "make_payment", payload: { studentId: s.student_id, studentName: `${s.first_name} ${s.last_name}` } })}
                  >Make Payment</Button>
                  {!s.auto_pay && (
                    <Button size="small" variant="outlined"
                      onClick={() => setDlg({ kind: "auto_pay_setup", payload: { studentId: s.student_id, studentName: `${s.first_name} ${s.last_name}`, outstanding: s.outstanding_balance } })}
                    >Set Up Auto-Pay</Button>
                  )}
                  {s.auto_pay && (
                    <Button size="small" variant="outlined" color="warning"
                      onClick={() => setDlg({ kind: "auto_pay_manage", payload: { studentId: s.student_id, agreementId: s.auto_pay!.agreement_id, payerParentId: s.auto_pay!.payer_parent_id } })}
                    >Manage Auto-Pay</Button>
                  )}
                </Stack>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Recent payments */}
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
        <ReceiptLongOutlinedIcon sx={{ verticalAlign: "text-bottom", mr: 0.5 }} />
        Recent Payments
      </Typography>
      {loading ? (
        <Skeleton variant="rounded" height={80} />
      ) : payments.length === 0 ? (
        <Alert severity="info">No payments yet.</Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Method</TableCell>
                <TableCell>Txn #</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>{fmtDate(p.paymentDate)}</TableCell>
                  <TableCell>{[p.student_first, p.student_last].filter(Boolean).join(" ") || "—"}</TableCell>
                  <TableCell>{p.invoiceNumber ?? "—"}</TableCell>
                  <TableCell align="right"><strong>{money(p.amount)}</strong></TableCell>
                  <TableCell>{p.cardBrand ? `${prettyCardBrand(p.cardBrand)} •••• ${p.cardLast4}` : (p.type ?? "—")}</TableCell>
                  <TableCell>{p.provider_transaction_id ?? p.transactionId ?? "—"}</TableCell>
                  <TableCell>{p.source ?? "—"}</TableCell>
                  <TableCell>{p.status ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogs — mount when active */}
      {dlg?.kind === "add_card" && parentCountry && (
        <AddCardDialog
          open
          parentId={parentId}
          parentCountry={parentCountry}
          onClose={() => setDlg(null)}
          onSuccess={() => { setDlg(null); fetchAll(); }}
        />
      )}
      {dlg?.kind === "add_bank" && (
        <AddBankAccountDialog
          open
          parentId={parentId}
          onClose={() => setDlg(null)}
          onSuccess={() => { setDlg(null); fetchAll(); }}
        />
      )}
      {dlg?.kind === "edit_card" && (
        <EditCardDialog
          open
          parentId={parentId}
          card={dlg.payload}
          onClose={() => setDlg(null)}
          onSuccess={() => { setDlg(null); fetchAll(); }}
        />
      )}
      {dlg?.kind === "delete_card" && (
        <ConfirmDialog
          open
          title="Delete Card"
          message={`Delete ${prettyCardBrand(dlg.payload.cardBrand)} •••• ${dlg.payload.cardLast4}? If this card is used by an active auto-pay agreement the delete will be refused; change or cancel the agreement first.`}
          confirmLabel="Delete"
          confirmColor="error"
          onClose={() => setDlg(null)}
          onConfirm={async () => {
            try {
              await paymentsApi.deleteMethod(parentId, dlg.payload.id);
              setDlg(null); fetchAll();
            } catch (e: any) {
              setError(e?.message ?? "Delete failed");
              setDlg(null);
            }
          }}
        />
      )}
      {dlg?.kind === "make_payment" && (
        <MakePaymentDialog
          open
          parentId={parentId}
          studentId={dlg.payload.studentId}
          studentName={dlg.payload.studentName}
          onClose={() => setDlg(null)}
          onSuccess={() => { setDlg(null); fetchAll(); }}
        />
      )}
      {dlg?.kind === "auto_pay_setup" && (
        <AutoPaySetupDialog
          open
          parentId={parentId}
          studentId={dlg.payload.studentId}
          studentName={dlg.payload.studentName}
          onClose={() => setDlg(null)}
          onSuccess={() => { setDlg(null); fetchAll(); }}
        />
      )}
      {dlg?.kind === "auto_pay_manage" && (
        <AutoPayManageDialog
          open
          parentId={parentId}
          studentId={dlg.payload.studentId}
          agreementId={dlg.payload.agreementId}
          onClose={() => setDlg(null)}
          onSuccess={() => { setDlg(null); fetchAll(); }}
        />
      )}
    </Box>
  );
};

export default PaymentsBillingSection;
